import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatPrice(amount, currency = "RWF") {
  const n = Number(amount ?? 0);
  return `${n.toLocaleString()} ${currency}`;
}

// Today's date where the user is — toISOString() is UTC, which is still
// "yesterday" in Kigali until 02:00.
export function todayISO(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

// "2026-10-11" → "Sun 11 Oct 2026"
export function formatDay(iso) {
  if (!iso) return "";
  return new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// How soon an appointment is due, from its date and start time:
// { key: "passed" | "minutes" | "today" | "tomorrow" | "days", count } —
// for "passed", count is the minutes since it started.
export function appointmentDue(date, startTime, now = new Date()) {
  if (!date || !startTime) return null;
  const day = String(date).slice(0, 10);
  const start = new Date(`${day}T${String(startTime).slice(0, 5)}:00`);
  if (Number.isNaN(start.getTime())) return null;
  const minutes = Math.ceil((start - now) / 60000);
  if (minutes <= 0) return { key: "passed", count: -minutes };
  if (minutes < 60) return { key: "minutes", count: minutes };
  const days = Math.round((new Date(`${day}T12:00:00`) - new Date(`${todayISO(now)}T12:00:00`)) / 86400000);
  if (days <= 0) return { key: "today" };
  if (days === 1) return { key: "tomorrow" };
  return { key: "days", count: days };
}

// What the customer is told about cancelling, before and after booking.
export function cancellationTerms(freeHours, fee) {
  const late =
    Number(fee) > 0
      ? `After that, a cancellation fee of ${formatPrice(fee)} applies, and the same if you don't show up.`
      : "After that, or if you don't show up, what you paid may not be refunded.";
  return `You can cancel or reschedule free of charge up to ${freeHours} hours before your appointment. ${late}`;
}

export function formatDuration(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h}h ${m}min`;
  if (h) return `${h}h`;
  return `${m}min`;
}

const API_ORIGIN = (process.env.REACT_APP_API_URL || "http://localhost:8000/api").replace(/\/api\/?$/, "");

// Uploaded photos come back as "/storage/..." relative paths; seeded/demo
// photos are already full URLs (e.g. Unsplash) — pass those through as-is.
export function resolveMediaUrl(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ORIGIN}${url}`;
}

// Every field's first validation message from an API error, or its
// message, so a form can say exactly what to fix.
export function apiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  const fields = Object.values(data?.errors || {}).map((messages) => messages?.[0]).filter(Boolean);
  return fields.length ? [...new Set(fields)].join(" ") : data?.message || fallback;
}
